'use client';
import { NextRouter } from 'next/router';
import { PropsWithChildren, useContext, createContext, JSX } from 'react';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

export type ParsedQuery = Record<string, string | string[]>;

type PagesContextValues = {
  push: NextRouter['push'] | AppRouterInstance['push'];
  pathname: string | null;
  params: Record<string, string | string[]>;
  query: ParsedQuery;
  back: () => void;
  refresh: () => void;
  asPath: string;
};

const PagesContext = createContext<PagesContextValues>(
  {} as PagesContextValues,
);

type Props = {
  routerPush: (url: string) => void; // Have to make sure it's compatible with both NextRouter and AppRouterInstance
  routerQuery: ParsedQuery;
  routerParams: ParsedQuery;
  routerPathname: NextRouter['pathname'] | null;
  routerBack: () => void;
  routerRefresh: () => void;
  asPath: string;
};

/**
 * PagesProvider.
 */
export const PagesProvider = (props: PropsWithChildren<Props>): JSX.Element => {
  const {
    children,
    asPath,
    routerParams, // The params associated with next routing, i.e. /learn/[concept] -> { concept: 'some-concept' }
    routerQuery, // The searchParams from the url, i.e. /learn/some-concept?some-query=1 -> { some-query: '1' }
    routerPathname,
    routerBack,
    routerPush,
    routerRefresh,
  } = props;

  return (
    <PagesContext.Provider
      value={{
        pathname: routerPathname,
        query: routerQuery,
        params: routerParams || {},
        push: routerPush,
        back: routerBack,
        refresh: routerRefresh,
        asPath,
      }}
    >
      {children}
    </PagesContext.Provider>
  );
};

/**
 * Returns values of SearchContext.
 */
export const usePagesContext = () => useContext(PagesContext);
